import { useAppDispatch, useAppSelector } from 'hooks/redux';
import type { List } from 'immutable';
// ROOT
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Grid, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
// MUI
import { makeStyles } from 'tss-react/mui';

import BillScoutLoadingView from 'components/Dialogs/BillScout/BillScoutLoadingView';
import { showAddScheduledTransactionDialog } from 'components/Dialogs/ScheduledTransactionAddDialog/actions';
// COMPONENTS
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import Dump from 'components/Dump';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import AmountField, { ShowSignEnum } from 'components/QuickenControls/AmountField';
import ZeroStateView from 'components/ZeroStateView';

// APP-COMPONENTS
import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { actionHelpers } from 'app-components/flux/core';
import { freeToSpendTypes } from 'app-components/flux/free-to-spend';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
} from 'app-components/flux/scheduled-transactions';
import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';
// ASSETS
import AmazedStar from 'assets/illustrations/Amazed-Star-New.svg';
import SpendingPlanBills from 'assets/images/spendingPlan/SpendingPlanBills.svg';
import SpendingPlanBillsSmall from 'assets/images/spendingPlan/SpendingPlanBillsSmall.svg';
import SuccessCheck from 'assets/images/spendingPlan/SuccessCheck.svg';
import SimpleContentView from '../SimpleContentView';

const useStyles = makeStyles()((theme) => ({
  billsSeriesRow: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    minWidth: '100%',
    minHeight: 60,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
  },
  simpleContent: {
    marginTop: 'auto',
    marginBottom: 'auto',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  txnImg: {
    width: 62,
    height: 62,
  },
  headerRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  gridContainer: {
    minWidth: '100%',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  textAlignRight: {
    textAlign: 'right',
  },
  rightAlign: {
    justifyContent: 'flex-end',
    display: 'flex',
    flexDirection: 'row',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
  },
  payee: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  amountRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  bold: {
    fontWeight: 500,
  },
  skipPadding: {
    marginRight: theme.spacing(2),
  },
}));

export const billsIndexEnums = Object.freeze({
  PRE_RECURRING: 'PRE_RECURRING',
  ADD_RECURRING: 'ADD_RECURRING',
  SUCCESS: 'SUCCESS',
});

export type BillsIndex = (typeof billsIndexEnums)[keyof typeof billsIndexEnums];

const typeEnums = scheduledTransactionsTypes.STTypeEnum;
const { GSFStageEnum } = freeToSpendTypes;

function BillsSeriesRow({
  key,
  series,
  seriesIdsToChange,
  setSeriesIdsToChange,
}: {
  key: string;
  series: any;
  seriesIdsToChange: string[];
  setSeriesIdsToChange: (items: string[]) => void;
}) {
  const { cx, classes } = useStyles();

  const categories = useAppSelector((state) => categoriesSelectors.getCategoriesById(state));

  const editSeries = useCallback(
    (series: scheduledTransactionsTypes.ScheduledTransactionType) => {
      const seriesId = series?.id ?? '';
      if (seriesIdsToChange.includes(seriesId)) {
        setSeriesIdsToChange(seriesIdsToChange.filter((myId) => myId !== seriesId));
      } else {
        setSeriesIdsToChange([...seriesIdsToChange, seriesId]);
      }
    },
    [seriesIdsToChange, setSeriesIdsToChange],
  );

  const isChangedToInclude =
    series?.excludeF2S === 'NONE'
      ? !seriesIdsToChange.includes(series?.id ?? '')
      : seriesIdsToChange.includes(series?.id ?? '');

  const frequency = series && customFrequencyUtils.recurrenceRowAlias(series.recurrence);

  return (
    <Grid
      key={key}
      container
      className={cx(classes.billsSeriesRow, isChangedToInclude && 'opacity')}
      onClick={() => editSeries(series)}
    >
      <Dump obj={series} />
      <Grid item xl={3} lg={3} md={3} sm={3} xs={3} className={classes.row}>
        <Checkbox checked={isChangedToInclude} />
        <Typography className={classes.payee} variant='paragraphRegular'>
          {series?.transaction?.payee}
        </Typography>
      </Grid>
      <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
        <Typography variant='paragraphRegular'>
          {categories.get(series?.transaction?.coa?.id ?? '')?.name ?? 'Uncategorized'}
        </Typography>
      </Grid>
      <Grid item xl={2} lg={2} md={2} sm={2} xs={2}>
        <Typography variant='paragraphRegular'>{frequency}</Typography>
      </Grid>
      <Grid item xl={2} lg={2} md={2} sm={2} xs={2} className={classes.rightAlign}>
        <AmountField
          showSign={ShowSignEnum.ALWAYS}
          variant='paragraphRegular'
          value={series?.transaction?.amount}
          className={classes.textAlignRight}
        />
      </Grid>
    </Grid>
  );
}

function TxnContent({ renderRows }: { renderRows: React.ReactNode }) {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.headerRow}>
        <Typography variant='bodyRegular'>
          We found the following&nbsp;
          <Typography component='span' variant='bodyMedium'>
            recurring bills
          </Typography>
          . Uncheck any you don’t want to include in your Spending Plan. You can always add any bills later.
        </Typography>
        <img src={SpendingPlanBillsSmall} className={classes.txnImg} />
      </div>
      <div className={classes.gridContainer}>{renderRows}</div>
    </>
  );
}

const sumSeriesIncludedAmounts = (
  idsToInvert: string[],
  series: List<scheduledTransactionsTypes.ScheduledTransactionType>,
) => {
  return series.reduce((sum: number, item: scheduledTransactionsTypes.ScheduledTransactionType) => {
    const isInverted = idsToInvert.includes(item?.id ?? '');

    // Original inclusion logic
    const originallyIncluded = item?.excludeF2S === 'NONE';

    // If inverted, flip inclusion
    const include = isInverted ? !originallyIncluded : originallyIncluded;

    return include ? sum + (item?.transaction?.amount || 0) : sum;
  }, 0);
};

function Bills() {
  const [_gettingStartedFlowPhase, setGettingStartedFlowPhase] = usePreferenceV2(
    'sSPGettingStartedFlowPhase',
    GSFStageEnum.COMPLETE,
  );

  const { classes, cx } = useStyles();
  const isMounted = useRef<boolean>();
  const dispatch = useAppDispatch();

  const institutionLoginsIdsNotRefreshing = useAppSelector(institutionLoginsSelectors.getIdsRefreshing)?.isEmpty();
  const { bills } = useAppSelector(scheduledTransactionsSelectors.getBillsAndIncome);
  const billsNoTransfers = useMemo(
    () => bills.filter((myBill) => !chartOfAccountsUtils.isTransferCOA(myBill?.transaction?.coa)),
    [bills],
  );

  const [screenIndex, setScreenIndex] = useState<BillsIndex>(billsIndexEnums.PRE_RECURRING);
  const [forceLoading, setForceLoading] = useState(false);
  const [forceError, setForceError] = useState(false);
  const [seriesIdsToChange, setSeriesIdsToChange] = useState<string[]>([]);

  // ========== TIMER REF ============
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  //  ================= VIEW STATE LOGIC =====================
  //  Views
  const scheduledView = useLoadState(scheduledTransactionsSelectors);
  const noRecurringBills = !billsNoTransfers?.size;
  const showAccountsRefreshingView =
    !institutionLoginsIdsNotRefreshing && screenIndex === billsIndexEnums.ADD_RECURRING;

  const mainView = useMemo(() => {
    const showSkeletonView = Boolean(loadState.skeletonStates.includes(scheduledView));

    // For some reason, the tryAgainStates must be separated into a separate const in order to prevent a lint error.
    const tryAgainStates = Boolean(loadState.tryAgainStates.includes(scheduledView));
    const showTryAgainView = Boolean(tryAgainStates || loadState.updateRequiredStates.includes(scheduledView));

    //  For some reason the noDataState is broken for fundingView, so it will be included in showReadyView and 'isEmpty' will be used to determine if there is no fundingData instead
    const showNoDataView = Boolean(loadState.noDataStates.includes(scheduledView));
    const showReadyView = Boolean(loadState.readyStates.includes(scheduledView));

    let myMainView = '';
    const loadingScreenOverride = showAccountsRefreshingView || forceLoading;
    if ((showReadyView || showNoDataView) && !loadingScreenOverride && !forceError) {
      myMainView = 'READY';
    } else if ((showTryAgainView && !loadingScreenOverride) || forceError) {
      myMainView = 'FAILED';
    } else if ((showSkeletonView || loadingScreenOverride) && !forceError) {
      myMainView = 'LOADING';
    }

    return myMainView;
  }, [showAccountsRefreshingView, forceError, forceLoading, scheduledView]);

  // =============== USE EFFECT VALUES =====================
  useEffect(() => {
    isMounted.current = true;
    tracker.track(tracker.events.startSPBillsVerification);
    return () => {
      isMounted.current = false;
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================== ACTION HANDLING =======================

  const onClose = useCallback(
    (forceContinue?: boolean) => {
      if (forceContinue || screenIndex === billsIndexEnums.SUCCESS) {
        tracker.track(tracker.events.completeSPBillsVerification);
        setGettingStartedFlowPhase(GSFStageEnum.PLANNED_INIT);
      }
      dispatch(rootUiActions.removeDialog());
    },
    [dispatch, screenIndex, setGettingStartedFlowPhase],
  );

  const setTimerForClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      onClose(true);
    }, 3000);
  }, [onClose]);

  const updateSeries = useCallback(() => {
    setForceLoading(true);

    const seriesUpdatePackage = billsNoTransfers
      .map((mySeries) => {
        const mySeriesId = mySeries?.id ?? '';
        const shouldUpdateF2S = seriesIdsToChange.includes(mySeriesId);
        const shouldUpdateType = mySeries?.type && mySeries.type !== scheduledTransactionsTypes.STTypeEnum.UNKNOWN;
        const shouldUpdateIsUserVerified = mySeries?.isUserVerified === false;

        let updatedSeries = mySeries;

        if (shouldUpdateF2S) {
          const updatedExcludeF2S = mySeries.excludeF2S === 'NONE' ? 'SRC' : 'NONE';
          updatedSeries = updatedSeries
            .set('excludeF2S', updatedExcludeF2S)
            .set('type', scheduledTransactionsTypes.STTypeEnum.UNKNOWN) // to avoid conflicts like BILL + isSubscription conflict
            .setIn(['transaction', 'isExcludedFromF2S'], !mySeries?.transaction?.isExcludedFromF2S);
        }

        if (shouldUpdateType) {
          updatedSeries = updatedSeries.set('type', scheduledTransactionsTypes.STTypeEnum.UNKNOWN); // to avoid conflicts like BILL + isSubscription conflict
        }

        if (shouldUpdateIsUserVerified) {
          updatedSeries = updatedSeries.set('isUserVerified', true);
        }

        return updatedSeries;
      })
      .toList()
      .toArray();

    actionHelpers
      .bindPromiseAction(
        dispatch,
        scheduledTransactionsActions.batchScheduledTransactions,
      )(seriesUpdatePackage)
      .then((_resp: any) => {
        if (isMounted.current === true) {
          setForceLoading(false);
          setScreenIndex(billsIndexEnums.SUCCESS);
          setTimerForClose();
        }
      })
      .catch((_error: any) => {
        setForceLoading(false);
        setForceError(true);
      });
  }, [setTimerForClose, billsNoTransfers, dispatch, seriesIdsToChange]);

  const onContinue = useCallback(() => {
    if (screenIndex === billsIndexEnums.PRE_RECURRING) {
      setScreenIndex(billsIndexEnums.ADD_RECURRING);
    }
    if (screenIndex === billsIndexEnums.ADD_RECURRING) {
      if (noRecurringBills) {
        onClose(true);
      } else {
        updateSeries();
      }
    }
    if (screenIndex === billsIndexEnums.SUCCESS) {
      onClose();
    }
  }, [screenIndex, noRecurringBills, updateSeries, onClose]);

  const addAccountFlow = useCallback(() => {
    dispatch(
      accountDiscoveryActions.doAccountDiscovery({
        initiator: 'spending plan GSF',
        mode: 'ADD-FI',
      }),
    );
  }, [dispatch]);

  const primaryButtonPressed = useCallback(() => {
    if (noRecurringBills && screenIndex === billsIndexEnums.ADD_RECURRING) {
      addAccountFlow();
    } else {
      onContinue();
    }
  }, [addAccountFlow, noRecurringBills, onContinue, screenIndex]);

  const addBills = useCallback(() => {
    dispatch(showAddScheduledTransactionDialog({ dialogType: typeEnums.BILL }));
  }, [dispatch]);

  // ====================== RENDERING =======================

  const disableContinue = screenIndex === billsIndexEnums.SUCCESS || forceLoading;
  let continueText = '';
  if (screenIndex === billsIndexEnums.PRE_RECURRING) continueText = 'Continue';
  if (screenIndex === billsIndexEnums.ADD_RECURRING && !noRecurringBills) continueText = 'Add bills';
  if (screenIndex === billsIndexEnums.ADD_RECURRING && noRecurringBills) continueText = 'Connect accounts';
  if (screenIndex === billsIndexEnums.SUCCESS) continueText = '';

  let tertiaryText = '';
  if (screenIndex === billsIndexEnums.PRE_RECURRING) tertiaryText = '';
  if (screenIndex === billsIndexEnums.ADD_RECURRING && !noRecurringBills) tertiaryText = 'Add missing bills';
  if (screenIndex === billsIndexEnums.SUCCESS) tertiaryText = '';

  let dialogTitle = '';
  if (screenIndex === billsIndexEnums.PRE_RECURRING) dialogTitle = '';
  if (screenIndex === billsIndexEnums.ADD_RECURRING) dialogTitle = 'Add bills to Spending Plan';
  if (screenIndex === billsIndexEnums.SUCCESS) dialogTitle = '';

  const renderSeriesRows = billsNoTransfers.map((series, index) => (
    <BillsSeriesRow
      series={series}
      setSeriesIdsToChange={setSeriesIdsToChange}
      seriesIdsToChange={seriesIdsToChange}
      key={`series-${index}`}
    />
  ));

  const renderScreen = useMemo(() => {
    if (mainView === 'LOADING') {
      return (
        <div className={classes.simpleContent}>
          {showAccountsRefreshingView ? <BillScoutLoadingView loading={true} /> : <LoadingView size={100} />}
        </div>
      );
    }
    if (mainView === 'FAILED') {
      return (
        <div className={classes.simpleContent}>
          <ZeroStateView
            id='gsf-failure'
            title='Data has failed to load'
            description={'Data has failed to load, please refresh and try again'}
            qIllustration='planeCrash'
          />
        </div>
      );
    }
    if (mainView === 'READY') {
      if (screenIndex === billsIndexEnums.PRE_RECURRING) {
        return (
          <SimpleContentView
            image={<img width='128px' height='128px' src={SpendingPlanBills} />}
            mainText={'Verify bills to include'}
            secondaryText={
              'We’ll deduct bills and subscriptions from your income—this will ensure all your important bills are accounted for.'
            }
          />
        );
      }
      if (screenIndex === billsIndexEnums.ADD_RECURRING) {
        if (noRecurringBills) {
          return (
            <SimpleContentView
              mainVariant={'headlineRegular'}
              image={<img width='168px' height='168px' src={AmazedStar} />}
              mainText={'No bills found!'}
              secondaryText={'Connect any accounts that you pay bills with to Simplifi or manually add your bills.'}
            />
          );
        }
        return <TxnContent renderRows={renderSeriesRows} />;
      }
      if (screenIndex === billsIndexEnums.SUCCESS) {
        return (
          <SimpleContentView
            image={<img width='128px' height='128px' src={SuccessCheck} />}
            mainText={'Sweet!'}
            secondaryText={'We’ve added your bills to your Spending Plan!'}
          />
        );
      }
    }
    return null;
  }, [mainView, classes.simpleContent, showAccountsRefreshingView, screenIndex, noRecurringBills, renderSeriesRows]);

  const zeroStateButtons = useMemo(
    () => (
      <>
        <QButton variant='text' onClick={onContinue} className={classes.skipPadding}>
          Skip for now
        </QButton>
        <QButton variant='outlined' onClick={addBills}>
          Manually Add Bills
        </QButton>
      </>
    ),
    [classes.skipPadding, addBills, onContinue],
  );

  const amountRow = useCallback(
    (amount: number) => (
      <>
        <Typography variant='paragraphRegular'>Total this month:&nbsp;</Typography>
        <AmountField
          showSign={ShowSignEnum.ALWAYS}
          variant='paragraphMedium'
          value={amount}
          className={cx(classes.textAlignRight, classes.bold)}
        />
      </>
    ),
    [classes.bold, classes.textAlignRight, cx],
  );

  const secondaryNode = useMemo(() => {
    if (noRecurringBills && screenIndex === billsIndexEnums.ADD_RECURRING) {
      return <div className={classes.amountRow}>{zeroStateButtons}</div>;
    }
    if (!noRecurringBills && screenIndex === billsIndexEnums.ADD_RECURRING) {
      return (
        <div className={classes.amountRow}>
          {amountRow(sumSeriesIncludedAmounts(seriesIdsToChange, billsNoTransfers.toList()))}
        </div>
      );
    }
    return null;
  }, [
    amountRow,
    billsNoTransfers,
    classes.amountRow,
    noRecurringBills,
    screenIndex,
    seriesIdsToChange,
    zeroStateButtons,
  ]);

  return (
    <StdDialog
      onClose={() => onClose()}
      open={true}
      sharedcomponentid={'FIRST_TIME_USE_DIALOG_V2'}
      showCloseButton={screenIndex !== billsIndexEnums.SUCCESS}
      size='md'
      title={dialogTitle}
      PaperProps={{
        sx: { overflowY: 'hidden' },
      }}
    >
      <StdDialogContent
        id='spending_plan_bills_dialog'
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {renderScreen}
      </StdDialogContent>
      {screenIndex !== billsIndexEnums.SUCCESS && mainView === 'READY' && (
        <StdDialogActions
          primary={{
            id: 'primary-button',
            disabled: disableContinue,
            children: continueText,
            onClick: primaryButtonPressed,
            variant: 'contained',
          }}
          secondaryNode={secondaryNode}
          tertiaryAction={{
            id: 'tertiary-button',
            children: tertiaryText,
            variant: 'text',
            onClick: addBills,
          }}
        />
      )}
    </StdDialog>
  );
}

export default Bills;
